import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { DesktopConfig, MfitChatChoice, MfitChatConfig, MfitChatMessage } from "../../scenario/types";
import { Desktop } from "../Desktop";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import { Avatar, InfoRows, IpadFrame, type MfitDone, type MfitGuideFn } from "./shared";
import { tx } from "../../lib/tx";

/**
 * Teams / Slack chat for "The Message From IT".
 *  - frame "desktop": drawn inside the office PC's Windows desktop (the app
 *    icon pulses, the learner opens it). mode "intranet" is the browser with
 *    the IT security-minute notice instead of a chat.
 *  - frame "ipad": a lock-screen notification glued to the beach-bar iPad;
 *    tapping it opens a readable iPad with the Slack DM.
 * All copy comes from the spec; the top-right guide text is `config.guide`
 * keyed by the stage this component reports.
 */
type Stage =
  | "desktop"
  | "intranet"
  | "video"
  | "chat"
  | "sender"
  | "link"
  | "result"
  | "sent"
  | "status"
  | "lock"
  | "outro"
  | "ipad_lock"
  | "ipad_away"
  | "review"
  | "review_sender";

const DESK_APPS = [
  { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  { id: "outlook", label: "Outlook", icon: "outlook", opens: "outlook" },
  { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
  { id: "slack", label: "Slack", icon: "slack", opens: "slack" },
  { id: "files", label: "File Explorer", icon: "files", opens: "files" },
];

export function MfitChat({
  config,
  onDone,
  onGuideChange,
  glueStyle,
}: {
  config: MfitChatConfig;
  onDone: (d: MfitDone) => void;
  onGuideChange?: MfitGuideFn;
  /** iPad frame: App's projected tablet box (null while the tablet is off-screen). */
  glueStyle?: CSSProperties | null;
}) {
  const isIpad = config.frame === "ipad";
  const skin = config.skin ?? "slack";
  const [stage, setStage] = useState<Stage>(config.review ? "review" : isIpad ? "ipad_lock" : "desktop");
  const [expanded, setExpanded] = useState(false);
  const [extra, setExtra] = useState<MfitChatMessage[]>([]);
  const [picked, setPicked] = useState<MfitChatChoice | null>(null);
  const [statusSet, setStatusSet] = useState(false);
  const [outroStep, setOutroStep] = useState(0);
  const doneRef = useRef(false);
  const voice = { voice: config.voiceId || "nova", lang: activeTtsLang() };

  const finish = (d: MfitDone) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(d);
  };

  // Prefetch the one spoken line (the film placeholder tip) so the voice
  // prerender records it even though it plays only after a click.
  useEffect(() => {
    const tip = config.intranet?.video.tip;
    if (tip && !config.intranet?.video.src) void audio.prefetchSpeech(tip, voice);
    if (config.notice) {
      const t = window.setTimeout(() => audio.ui("alert"), 450);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => audio.cancelSpeech(), []);

  // iPad: while the tablet is off-screen the lock screen cannot be tapped.
  const effectiveStage: Stage = isIpad && !expanded && !glueStyle ? "ipad_away" : stage;
  useEffect(() => {
    onGuideChange?.(config.guide?.[effectiveStage] ?? null);
  }, [effectiveStage, config.guide, onGuideChange]);
  useEffect(() => () => onGuideChange?.(null), [onGuideChange]);

  // Slack handover outro: lock screen -> status line -> fade -> ping -> done.
  useEffect(() => {
    if (stage !== "outro") return;
    const steps = config.outro?.lines.length ?? 0;
    const timers: number[] = [];
    for (let i = 1; i <= steps; i++) timers.push(window.setTimeout(() => setOutroStep(i), i * 1500));
    if (config.outro?.ping) timers.push(window.setTimeout(() => audio.ui("message"), steps * 1500 + 700));
    timers.push(window.setTimeout(() => finish({ key: "done" }), steps * 1500 + 2600));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const messages = useMemo(() => [...config.messages, ...extra], [config.messages, extra]);

  const choose = (c: MfitChatChoice) => {
    if (c.inspect) {
      setStage(c.id.includes("link") ? "link" : "sender");
      return;
    }
    setPicked(c);
    if (c.reply?.length) {
      // Replies arrive one after another, like a real chat.
      c.reply.forEach((m, i) =>
        window.setTimeout(() => {
          setExtra((cur) => [...cur, m]);
          audio.ui("message");
        }, 700 + i * 1300),
      );
      window.setTimeout(() => (c.result ? setStage("result") : finish({ key: c.key, eventId: c.eventId, evidence: c.evidence })), 900 + c.reply.length * 1300);
      return;
    }
    if (c.result) setStage("result");
    else finish({ key: c.key, eventId: c.eventId, evidence: c.evidence });
  };

  const choicesBlock = config.choices?.length ? (
    <div className={`mfit-choices${isIpad ? " is-ipad" : ""}`}>
      {config.choicesPrompt && <div className="mfit-choices-q">{config.choicesPrompt}</div>}
      <div className="mfit-choices-grid">
        {config.choices.map((c, i, all) => (
          <button
            key={c.id}
            type="button"
            className={`mfit-choice${picked || config.review ? "" : " is-sweep"}`}
            style={{ "--i": i, "--n": all.length } as CSSProperties}
            disabled={!!picked}
            onClick={() => choose(c)}
          >
            <span className="mfit-choice-l">{c.label}</span>
            {c.blurb && <span className="mfit-choice-b">{c.blurb}</span>}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  const panel =
    stage === "sender" && config.senderCard ? (
      <InfoPanel card={config.senderCard} onClose={() => setStage("chat")} />
    ) : stage === "link" && config.linkCard ? (
      <InfoPanel card={config.linkCard} onClose={() => setStage("chat")} />
    ) : stage === "result" && picked?.result ? (
      <div className={`mfit-result is-${picked.result.tone ?? "neutral"}`}>
        <div className="mfit-result-t">{picked.result.title}</div>
        <p>{picked.result.body}</p>
        <button
          type="button"
          className="mfit-btn mfit-btn--primary cf-pulse-target"
          onClick={() => finish({ key: picked.key, eventId: picked.eventId, evidence: picked.evidence })}
        >
          {picked.result.continueLabel ?? tx("Continue")}
        </button>
      </div>
    ) : null;

  // ------------------------------------------------------------------ iPad
  if (isIpad) {
    if (!expanded) {
      if (!glueStyle) return <div className="mfit-away-hint">{config.guide?.ipad_away?.body ?? tx("Look back at the iPad on the bar.")}</div>;
      return (
        <IpadFrame glueStyle={glueStyle} expanded={false}>
          <div className="mfit-lock">
            <div className="mfit-lock-time">{config.lockTime ?? ""}</div>
            <div className="mfit-lock-date">{config.lockDate ?? ""}</div>
            {config.notice && (
              <button
                type="button"
                className="mfit-lock-notif cf-pulse-target"
                aria-label={tx("Open the {app} notification", { app: config.notice.app })}
                onClick={() => {
                  setExpanded(true);
                  setStage("chat");
                }}
              >
                <span className={`mfit-app-ico is-${skin}`} aria-hidden="true" />
                <span className="mfit-lock-notif-txt">
                  <b>
                    {config.notice.app} · {config.notice.from}
                  </b>
                  <span>{config.notice.text}</span>
                </span>
                <span className="mfit-lock-notif-time">{config.notice.time ?? tx("now")}</span>
              </button>
            )}
          </div>
        </IpadFrame>
      );
    }
    return (
      <IpadFrame expanded clock={config.lockTime}>
        <div className={`mfit-slack is-ipad`}>
          <div className="mfit-slack-top">
            <span className="mfit-slack-back" aria-hidden="true">‹</span>
            <Avatar name={config.contact?.name ?? config.channel ?? ""} url={config.contact?.avatarUrl} size={30} />
            <button type="button" className="mfit-slack-who" onClick={() => config.senderCard && setStage("sender")}>
              <b>{config.contact?.name ?? config.channel}</b>
              <span>{config.contact?.role ?? config.channelTopic ?? ""}</span>
            </button>
            <span className="mfit-slack-ws">{config.workspace}</span>
          </div>
          <MessageList messages={messages} skin="slack" onOpenAttachment={() => {
            const open = config.choices?.find((c) => c.key === "open");
            if (open && !picked) choose(open);
          }} />
          {panel ?? (!picked && choicesBlock)}
        </div>
      </IpadFrame>
    );
  }

  // --------------------------------------------------------------- Desktop
  const appId = config.mode === "intranet" ? "browser" : skin;
  const deskConfig: DesktopConfig = {
    clock: config.clock,
    date: config.date,
    icons: DESK_APPS,
    taskbar: DESK_APPS,
  };
  const windowOpen = stage !== "desktop";

  return (
    <Desktop
      config={deskConfig}
      className="mfit-desk"
      highlightAppId={stage === "desktop" ? appId : undefined}
      openAppIds={windowOpen ? [appId] : []}
      activeAppId={windowOpen ? appId : null}
      badgeAppIds={stage === "desktop" && config.notice ? [appId] : []}
      onOpen={(id) => {
        if (stage === "desktop" && id === appId) setStage(config.mode === "intranet" ? "intranet" : "chat");
      }}
    >
      {stage === "desktop" && config.notice && (
        <button type="button" className={`mfit-toast is-${skin}`} onClick={() => setStage("chat")}>
          <span className={`mfit-app-ico is-${skin}`} aria-hidden="true" />
          <span className="mfit-toast-txt">
            <b>{config.notice.from}</b>
            <span>{config.notice.text}</span>
          </span>
        </button>
      )}

      {config.mode === "intranet" && windowOpen && config.intranet && (
        <IntranetWindow
          cfg={config.intranet}
          stage={stage}
          voice={voice}
          onWatch={() => setStage("video")}
          onDismiss={() => finish({ key: "dismissed" })}
          onWatched={() => finish({ key: "watched", eventId: config.intranet?.watchEventId, evidence: "Watched the IT security minute" })}
        />
      )}

      {config.mode === "chat" && windowOpen && stage !== "outro" && skin === "teams" && (
        <div className="mfit-win mfit-teams">
          <WinBar title={tx("Microsoft Teams")} />
          <div className="mfit-teams-body">
            <nav className="mfit-teams-rail" aria-hidden="true">
              {["Activity", "Chat", "Teams", "Calendar", "Files"].map((n) => (
                <span key={n} className={n === "Chat" ? "is-on" : ""}>
                  <i />
                  {n}
                </span>
              ))}
            </nav>
            <aside className="mfit-teams-list">
              <div className="mfit-teams-list-h">{tx("Chat")}</div>
              <div className="mfit-teams-item is-on">
                <Avatar name={config.contact?.name ?? ""} initials={config.contact?.initials} size={28} tone="#6264a7" />
                <span>
                  <b>{config.contact?.name}</b>
                  {config.contact?.external && <em className="mfit-ext">{tx("External")}</em>}
                  <small>{config.messages[config.messages.length - 1]?.text.slice(0, 34)}…</small>
                </span>
              </div>
              <div className="mfit-teams-item">
                <Avatar name="Nina Patel" size={28} tone="#8a6f3e" />
                <span>
                  <b>{tx("Nina Patel")}</b>
                  <small>{tx("See you after the break!")}</small>
                </span>
              </div>
            </aside>
            <section className="mfit-teams-main">
              <header className="mfit-teams-head">
                <Avatar name={config.contact?.name ?? ""} initials={config.contact?.initials} size={30} tone="#6264a7" />
                <button type="button" className="mfit-teams-who" onClick={() => config.senderCard && setStage("sender")}>
                  <b>{config.contact?.name}</b>
                  {config.contact?.external && <em className="mfit-ext">{tx("External")}</em>}
                </button>
                <span className="mfit-teams-tabs" aria-hidden="true">
                  <span className="is-on">{tx("Chat")}</span>
                  <span>{tx("Files")}</span>
                </span>
              </header>
              <MessageList messages={messages} skin="teams" />
              {panel ?? (!picked && choicesBlock)}
            </section>
          </div>
        </div>
      )}

      {config.mode === "chat" && windowOpen && stage !== "outro" && skin === "slack" && (
        <div className="mfit-win mfit-slackwin">
          <WinBar title={tx("Slack — {v}", { v: config.workspace ?? "" })} />
          <div className="mfit-slack">
            <aside className="mfit-slack-side">
              <div className="mfit-slack-ws-name">{config.workspace}</div>
              <div className="mfit-slack-sec">{tx("Channels")}</div>
              {["general", "supplier-renewal", "finance-ops", "random"].map((c) => (
                <span key={c} className={`mfit-slack-ch${`#${c}` === config.channel ? " is-on" : ""}`}>
                  # {c}
                </span>
              ))}
              <div className="mfit-slack-sec">{tx("Direct messages")}</div>
              <span className="mfit-slack-ch">{tx("Nina Patel")}</span>
              <span className="mfit-slack-ch">{tx("Finance team")}</span>
              <div className="mfit-slack-me">
                <Avatar name={config.me?.name ?? "Alex"} url={config.me?.avatarUrl} size={24} />
                <span>
                  <b>{config.me?.name}</b>
                  <small>{statusSet && config.statusStep ? `${config.statusStep.emoji ?? ""} ${config.statusStep.text}` : tx("Active")}</small>
                </span>
              </div>
            </aside>
            <section className="mfit-slack-main">
              <header className="mfit-slack-head">
                <b>{config.channel}</b>
                {config.channelTopic && <span>{config.channelTopic}</span>}
              </header>
              <MessageList messages={messages} skin="slack" />
              <div className="mfit-slack-foot">
                {config.compose && stage === "chat" && (
                  <div className="mfit-compose">
                    <div className="mfit-compose-tb" aria-hidden="true">
                      <b>{tx("B")}</b>
                      <i>{tx("I")}</i>
                      <s>{tx("S")}</s>
                      <span>{"</>"}</span>
                      <span>{"\u2261"}</span>
                    </div>
                    <div className="mfit-compose-txt">{config.compose.text}</div>
                    <button
                      type="button"
                      className="mfit-btn mfit-btn--slack cf-pulse-target"
                      onClick={() => {
                        setExtra((cur) => [...cur, { id: "mfit-self", from: config.me?.name ?? "Alex", time: config.compose!.time ?? "", text: config.compose!.text, self: true, avatarUrl: config.me?.avatarUrl }]);
                        audio.click();
                        setStage(config.statusStep ? "status" : "lock");
                      }}
                    >
                      {config.compose.sendLabel ?? tx("Send")}
                    </button>
                  </div>
                )}
                {stage === "status" && config.statusStep && (
                  <div className="mfit-status-step">
                    <div>
                      <b>{config.statusStep.label}</b>
                      <span>
                        {config.statusStep.emoji} {config.statusStep.text}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="mfit-btn mfit-btn--slack cf-pulse-target"
                      onClick={() => {
                        setStatusSet(true);
                        setStage("lock");
                      }}
                    >
                      {config.statusStep.setLabel ?? tx("Set status")}
                    </button>
                  </div>
                )}
                {stage === "lock" && (
                  <div className="mfit-status-step">
                    <div>
                      <b>{tx("All done for today")}</b>
                      <span>{tx("Close Slack and lock the PC before you go.")}</span>
                    </div>
                    <button type="button" className="mfit-btn mfit-btn--dark cf-pulse-target" onClick={() => setStage("outro")}>
                      {config.lockLabel ?? tx("Lock PC and leave")}
                    </button>
                  </div>
                )}
                {!config.compose && panel}
                {!config.compose && !panel && !picked && choicesBlock}
              </div>
            </section>
          </div>
        </div>
      )}

      {config.review && (stage === "review" || stage === "review_sender") && (
        <ReviewModal
          cfg={config.review}
          contact={config.contact}
          account={config.senderCard?.rows.find((r) => r.label === "Account")?.value}
          message={config.messages[0]}
          revealed={stage === "review_sender"}
          onReveal={() => setStage("review_sender")}
          onDone={() => finish({ key: "done" })}
        />
      )}

      {stage === "outro" && (
        <div className="mfit-outro" aria-live="polite">
          <div className="mfit-outro-clock">{config.clock}</div>
          {config.outro?.lines.map((l, i) => (
            <div key={l} className={`mfit-outro-line${outroStep > i ? " is-on" : ""}`}>
              {l}
            </div>
          ))}
          <div className={`mfit-outro-black${outroStep >= (config.outro?.lines.length ?? 0) ? " is-on" : ""}`} />
        </div>
      )}
    </Desktop>
  );
}

function WinBar({ title }: { title: string }) {
  return (
    <div className="mfit-winbar">
      <span>{title}</span>
      <span className="mfit-winbar-ctl" aria-hidden="true">
        <i>–</i>
        <i>▢</i>
        <i>✕</i>
      </span>
    </div>
  );
}

function MessageList({
  messages,
  skin,
  onOpenAttachment,
}: {
  messages: MfitChatMessage[];
  skin: "teams" | "slack";
  onOpenAttachment?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);
  return (
    <div className={`mfit-msgs is-${skin}`} ref={ref}>
      {messages.map((m) => (
        <div key={m.id} className={`mfit-msg${m.self ? " is-self" : ""}`}>
          <Avatar name={m.from} initials={m.initials} url={m.avatarUrl} size={skin === "teams" ? 28 : 32} tone={m.self ? "#2f7d6d" : undefined} />
          <div className="mfit-msg-b">
            <div className="mfit-msg-h">
              <b>{m.from}</b>
              <span>{m.time}</span>
            </div>
            <div className="mfit-msg-t">
              {m.text.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {m.attachment && (
              <button type="button" className="mfit-file" onClick={onOpenAttachment} disabled={!onOpenAttachment}>
                <span className="mfit-file-ico" aria-hidden="true">{tx("PDF")}</span>
                <span>
                  <b>{m.attachment.name}</b>
                  <small>
                    {m.attachment.source}
                    {m.attachment.size ? ` · ${m.attachment.size}` : ""}
                  </small>
                </span>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function InfoPanel({
  card,
  onClose,
}: {
  card: { title: string; rows: { label: string; value: string; warn?: boolean }[]; note?: string };
  onClose: () => void;
}) {
  return (
    <div className="mfit-info">
      <div className="mfit-info-t">{card.title}</div>
      <InfoRows rows={card.rows} />
      {card.note && <p className="mfit-info-n">{card.note}</p>}
      <button type="button" className="mfit-btn" onClick={onClose}>
        {tx("Back to the message")}
      </button>
    </div>
  );
}

function IntranetWindow({
  cfg,
  stage,
  voice,
  onWatch,
  onDismiss,
  onWatched,
}: {
  cfg: NonNullable<MfitChatConfig["intranet"]>;
  stage: Stage;
  voice: { voice: string; lang: string };
  onWatch: () => void;
  onDismiss: () => void;
  onWatched: () => void;
}) {
  const [tipDone, setTipDone] = useState(false);
  useEffect(() => {
    if (stage !== "video" || cfg.video.src) return;
    let alive = true;
    void audio.speak(cfg.video.tip, voice).then(() => alive && setTipDone(true));
    return () => {
      alive = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  return (
    <div className="mfit-win mfit-browser">
      <WinBar title={tx("{siteName} — Browser", { siteName: cfg.siteName })} />
      <div className="mfit-addr">
        <span aria-hidden="true">‹ › ⟳</span>
        <span className="mfit-addr-url">
          <i aria-hidden="true">🔒</i>
          {cfg.url}
        </span>
      </div>
      <div className="mfit-intra">
        <header className="mfit-intra-h">
          <b>{cfg.siteName}</b>
          <nav>{(cfg.navItems ?? []).map((n) => <span key={n}>{n}</span>)}</nav>
        </header>
        <div className="mfit-intra-body">
          {cfg.headline && <h2>{cfg.headline}</h2>}
          <div className="mfit-intra-news">
            {(cfg.news ?? []).map((n) => (
              <article key={n.title}>
                <b>{n.title}</b>
                <p>{n.body}</p>
              </article>
            ))}
          </div>
        </div>
        {stage === "intranet" && (
          <div className="mfit-notice" role="dialog" aria-label={cfg.notice.title}>
            <div className="mfit-notice-k">{cfg.notice.kicker}</div>
            <div className="mfit-notice-t">{cfg.notice.title}</div>
            <p>{cfg.notice.body}</p>
            <div className="mfit-notice-a">
              <button type="button" className="mfit-btn mfit-btn--primary" onClick={onWatch}>
                ▶ {cfg.notice.watchLabel}
              </button>
              <button type="button" className="mfit-btn" onClick={onDismiss}>
                {cfg.notice.dismissLabel}
              </button>
            </div>
          </div>
        )}
        {stage === "video" && (
          <div className="mfit-film">
            <div className="mfit-film-t">{cfg.video.title}</div>
            {cfg.video.src ? (
              <video src={cfg.video.src} poster={cfg.video.poster} controls autoPlay playsInline onEnded={onWatched} />
            ) : (
              <div className="mfit-film-ph">
                <div className="mfit-film-ph-k">{tx("Security minute")}</div>
                <div className="mfit-film-ph-tip">{cfg.video.tip}</div>
                {cfg.video.placeholderNote && <div className="mfit-film-ph-n">{cfg.video.placeholderNote}</div>}
              </div>
            )}
            <button type="button" className={`mfit-btn mfit-btn--primary${tipDone ? " cf-pulse-target" : ""}`} onClick={onWatched}>
              {cfg.video.closeLabel ?? tx("Close the video")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** "More details" second look: the same message in a modal; the learner
 *  clicks the sender and the warning signs are revealed one by one. */
function ReviewModal({
  cfg,
  contact,
  account,
  message,
  revealed,
  onReveal,
  onDone,
}: {
  cfg: NonNullable<MfitChatConfig["review"]>;
  contact?: MfitChatConfig["contact"];
  account?: string;
  message?: MfitChatMessage;
  revealed: boolean;
  onReveal: () => void;
  onDone: () => void;
}) {
  const name = contact?.name ?? message?.from ?? "";
  return (
    <div className="mfit-review" role="dialog" aria-label={cfg.title}>
      <div className="mfit-review-card">
        <div className="mfit-review-k">{cfg.kicker ?? tx("A second look")}</div>
        <div className="mfit-review-t">{cfg.title}</div>
        <p className="mfit-review-p">{revealed ? cfg.revealIntro : cfg.prompt}</p>
        <div className="mfit-review-msg">
          <div className="mfit-review-msg-h">
            <Avatar name={name} initials={contact?.initials ?? message?.initials} size={30} tone="#6264a7" />
            <button
              type="button"
              className={`mfit-review-who${revealed ? " is-open" : " cf-pulse-target"}`}
              onClick={onReveal}
              disabled={revealed}
            >
              <b>{name}</b>
              {contact?.external && <em className={`mfit-ext${revealed ? " is-flag" : ""}`}>{tx("External")}</em>}
            </button>
            {!revealed && <span className="mfit-review-hint">{tx("Click the name")}</span>}
            {revealed && account && <span className="mfit-review-acct">{account}</span>}
            <span className="mfit-review-time">{message?.time}</span>
          </div>
          <div className={`mfit-review-msg-t${revealed ? " is-short" : ""}`}>
            {(message?.text ?? "").split("\n\n").map((t, i) => (
              <p key={i}>{t}</p>
            ))}
          </div>
        </div>
        {revealed && (
          <ol className="mfit-review-issues">
            {cfg.issues.map((it, i) => (
              <li key={it.label} style={{ animationDelay: `${0.25 + i * 0.45}s` }}>
                <b>{it.label}</b>
                <span>{it.detail}</span>
              </li>
            ))}
          </ol>
        )}
        {revealed && (
          <div className="mfit-review-a">
            <button type="button" className="mfit-btn mfit-btn--primary cf-pulse-target" onClick={onDone}>
              {cfg.continueLabel ?? tx("Continue")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
